import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Chip, Grid, Select, MenuItem, TextField,
  IconButton, Collapse, Switch, FormControlLabel, Tooltip, Slider, InputAdornment,
  ToggleButton, ToggleButtonGroup, LinearProgress, Divider,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InventoryIcon from "@mui/icons-material/Inventory";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PrintIcon from "@mui/icons-material/Print";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

// ── Types ──
interface Breakdown { shop_id: number; shop_name: string; qty: number; unit: string; order_id: number; route_name: string; }
interface AiPrediction { product_id: number; shop_id: number; shop_name: string; predicted_qty: number; unit: string; confidence: number; trend: string; reason: string; }
interface ForecastItem { product_id: number; name: string; unit: string; confirmed_qty: number; predicted_adj: number; final_qty: number; orders_count: number; ai_confidence: number; ai_trend: string; ai_reason: string; breakdown: Breakdown[]; ai_predictions: AiPrediction[]; }
interface MissingShop { id: number; name: string; }
interface Summary { total_confirmed: number; total_predicted: number; total_final: number; unique_products: number; unique_restaurants: number; orders_count: number; total_restaurants_on_route: number; ordered_restaurants: number; missing_restaurants: MissingShop[]; }
interface RouteOption { id: number; name: string; }
interface RiskAlert { type: "warning" | "error" | "info"; icon: React.ReactNode; title: string; detail: string; }

// ── Styles ──
const cardSx = { p: 2.5, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", transition: "all 0.2s ease", "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)", transform: "translateY(-1px)" } };
const thSx = { fontWeight: 600, fontSize: 10, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fafbfc" };
const btnOutline = { borderRadius: 2.5, textTransform: "none" as const, fontSize: 12, fontWeight: 600, borderColor: "rgba(0,0,0,0.08)", color: "#64748b", "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" } };

const DemandForecast: React.FC = () => {
  const [deliveryDate, setDeliveryDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split("T")[0]; });
  const [routeId, setRouteId] = useState<string>("");
  const [includeAi, setIncludeAi] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ForecastItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [routes, setRoutes] = useState<RouteOption[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [generated, setGenerated] = useState(false);
  const [mode, setMode] = useState<"forecast" | "procurement">("forecast");
  const [bufferPct, setBufferPct] = useState(5);
  const [minConfidence, setMinConfidence] = useState(0);
  const [showMissing, setShowMissing] = useState(false);
  const [locked, setLocked] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);

  const getPriority = (item: ForecastItem, all: ForecastItem[]) => {
    const finals = all.map(d => d.final_qty).sort((a, b) => a - b);
    const med = finals[Math.floor(finals.length / 2)] || 1;
    if (item.final_qty >= med * 1.5 || item.ai_trend === "increasing") return { label: "High", color: "#ef4444", bg: "#fef2f2", dot: "#ef4444" };
    if (item.final_qty <= med * 0.5 && item.ai_trend !== "increasing") return { label: "Low", color: "#94a3b8", bg: "#f8fafc", dot: "#cbd5e1" };
    return { label: "Med", color: "#f59e0b", bg: "#fffbeb", dot: "#f59e0b" };
  };

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/route/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ searchText: "" }) });
        const json = await res.json();
        setRoutes((json?.data || []).filter((r: any) => !r.isDelete).map((r: any) => ({ id: r.id, name: r.name })));
      } catch {}
    })();
  }, []);

  const generate = useCallback(async () => {
    setLoading(true); setShowAlerts(true);
    try {
      const res = await fetch(`${API_URL}/demand-forecast`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ delivery_date: deliveryDate, route_id: routeId || null, include_ai: includeAi }) });
      const json = await res.json();
      if (json.success) { setData(json.data || []); setSummary(json.summary || null); if (routes.length === 0 && json.routes) setRoutes(json.routes.map((r: any) => ({ id: r.id, name: r.name }))); setGenerated(true); }
    } catch {} finally { setLoading(false); }
  }, [deliveryDate, routeId, includeAi, routes.length]);

  const toggleExpand = (pid: number) => setExpanded(prev => { const n = new Set(prev); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });

  const filteredData = useMemo(() => minConfidence <= 0 ? data : data.filter(d => d.confirmed_qty > 0 || d.ai_confidence >= minConfidence), [data, minConfidence]);

  const getPurchaseQty = (item: ForecastItem) => {
    const base = mode === "procurement" && includeAi ? item.final_qty : item.confirmed_qty + (includeAi ? item.predicted_adj : 0);
    const buffer = mode === "procurement" ? Math.round(base * bufferPct / 100 * 10) / 10 : 0;
    return { base: Math.round(base * 10) / 10, buffer, total: Math.round((base + buffer) * 10) / 10 };
  };

  const procSummary = useMemo(() => {
    if (!summary) return null;
    const tBase = filteredData.reduce((s, d) => s + getPurchaseQty(d).base, 0);
    const tBuf = filteredData.reduce((s, d) => s + getPurchaseQty(d).buffer, 0);
    return { totalBase: Math.round(tBase * 10) / 10, totalBuffer: Math.round(tBuf * 10) / 10, totalPurchase: Math.round((tBase + tBuf) * 10) / 10 };
  }, [summary, filteredData, mode, bufferPct, includeAi]);

  const completeness = summary ? (summary.total_restaurants_on_route > 0 ? Math.round((summary.ordered_restaurants / summary.total_restaurants_on_route) * 100) : 0) : 0;
  const compColor = completeness >= 80 ? "#16a34a" : completeness >= 50 ? "#ea580c" : "#dc2626";
  const isProcure = mode === "procurement";

  const riskAlerts = useMemo((): RiskAlert[] => {
    if (!generated || !summary) return [];
    const a: RiskAlert[] = [];
    if (summary.missing_restaurants.length > 0 && completeness < 70) a.push({ type: "warning", icon: <StorefrontIcon sx={{ fontSize: 15 }} />, title: `${summary.missing_restaurants.length} restaurants haven't ordered`, detail: summary.missing_restaurants.map(m => m.name).join(", ") });
    const sp = filteredData.filter(d => d.ai_trend === "increasing" && d.predicted_adj > 0);
    if (sp.length > 0) a.push({ type: "info", icon: <TrendingUpIcon sx={{ fontSize: 15 }} />, title: `${sp.length} products trending up`, detail: sp.slice(0, 4).map(s => s.name).join(", ") + (sp.length > 4 ? ` +${sp.length - 4} more` : "") });
    const dr = filteredData.filter(d => d.ai_trend === "decreasing" && d.confirmed_qty > 0);
    if (dr.length > 0) a.push({ type: "warning", icon: <TrendingDownIcon sx={{ fontSize: 15 }} />, title: `${dr.length} products trending down`, detail: dr.slice(0, 4).map(s => s.name).join(", ") + (dr.length > 4 ? ` +${dr.length - 4} more` : "") });
    const po = filteredData.filter(d => d.confirmed_qty === 0 && d.predicted_adj > 0);
    if (po.length > 0) a.push({ type: "info", icon: <AutoAwesomeIcon sx={{ fontSize: 15 }} />, title: `${po.length} AI-predicted only`, detail: "No confirmed orders — based on historical patterns" });
    return a;
  }, [generated, summary, filteredData, completeness]);

  const dayLabel = deliveryDate ? new Date(deliveryDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "";
  const totals = useMemo(() => {
    const c = filteredData.reduce((s, d) => s + d.confirmed_qty, 0);
    const p = filteredData.reduce((s, d) => s + d.predicted_adj, 0);
    const b = filteredData.reduce((s, d) => s + getPurchaseQty(d).buffer, 0);
    const f = filteredData.reduce((s, d) => s + (isProcure ? getPurchaseQty(d).total : getPurchaseQty(d).base), 0);
    const o = filteredData.reduce((s, d) => s + d.orders_count, 0);
    return { confirmed: Math.round(c * 10) / 10, predicted: Math.round(p * 10) / 10, buffer: Math.round(b * 10) / 10, final: Math.round(f * 10) / 10, orders: o };
  }, [filteredData, isProcure, bufferPct, includeAi, mode]);
  const totalRest = summary?.total_restaurants_on_route || 0;
  const colCount = 5 + (includeAi ? 3 : 0) + (isProcure ? 1 : 0);

  const exportCSV = () => {
    if (!filteredData.length) return;
    const h = ["Product", "Unit", "Confirmed", ...(includeAi ? ["Predicted"] : []), ...(isProcure ? ["Buffer", "Purchase Qty"] : ["Final Qty"]), "Orders", "Priority", ...(includeAi ? ["Confidence", "Trend"] : [])];
    const rows = [h];
    filteredData.forEach(d => { const pq = getPurchaseQty(d); const pr = getPriority(d, filteredData); rows.push([d.name, d.unit, String(d.confirmed_qty), ...(includeAi ? [String(d.predicted_adj)] : []), ...(isProcure ? [String(pq.buffer), String(pq.total)] : [String(pq.base)]), String(d.orders_count), pr.label, ...(includeAi ? [`${d.ai_confidence}%`, d.ai_trend] : [])]); });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${mode}-${deliveryDate}.csv`; a.click();
  };

  const printForecast = () => {
    const win = window.open("", "_blank", "width=1200,height=900");
    if (!win) return;

    // Resolve absolute logo URL (webpack import string)
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const logoModule = require("../../Images/logo_main_black.webp");
    const logoSrc: string = (logoModule && (logoModule.default || logoModule)) as string;
    const logoUrl = logoSrc.startsWith("http") || logoSrc.startsWith("data:")
      ? logoSrc
      : `${window.location.origin}${logoSrc}`;

    const reportDate = dayjs().format("MMMM DD, YYYY");
    const preparedBy = localStorage.getItem("user_email") || localStorage.getItem("email") || "—";
    const routeName = routeId ? (routes.find(r => r.id === Number(routeId))?.name || "—") : "All Routes";
    const forecastDateLabel = deliveryDate ? dayjs(deliveryDate).format("MMM D, YYYY") : "—";
    const filterLabel = `${routeName} | ${forecastDateLabel}`;

    const totalProducts = filteredData.length;
    const totalConfirmed = filteredData.reduce((s, d) => s + Number(d.confirmed_qty || 0), 0);
    const totalPredicted = filteredData.reduce((s, d) => s + Number(d.predicted_adj || 0), 0);
    const totalFinal = filteredData.reduce((s, d) => s + Number(d.final_qty || 0), 0);

    const fmt = (n: number) => Math.round(n * 10) / 10;

    const trendCell = (t: string) => {
      const k = (t || "stable").toLowerCase();
      if (k === "increasing") return `<span class="trend up">▲ Increasing</span>`;
      if (k === "decreasing") return `<span class="trend down">▼ Decreasing</span>`;
      return `<span class="trend stable">— Stable</span>`;
    };
    const priorityCell = (label: string) => {
      const cls = label === "High" ? "high" : label === "Low" ? "low" : "med";
      return `<span class="priority ${cls}">${label}</span>`;
    };

    const rowsHtml = filteredData.map((d, i) => {
      const pr = getPriority(d, filteredData);
      return `
        <tr>
          <td class="num">${i + 1}</td>
          <td class="prod">${d.name}</td>
          <td>${d.unit || "—"}</td>
          <td class="right amt">${d.confirmed_qty ?? 0}</td>
          <td class="right pred">${d.predicted_adj > 0 ? `+${d.predicted_adj}` : (d.predicted_adj || 0)}</td>
          <td class="right final">${d.final_qty ?? 0}</td>
          <td class="right">${d.orders_count ?? 0}</td>
          <td>${priorityCell(pr.label)}</td>
          <td>${trendCell(d.ai_trend)}</td>
        </tr>
      `;
    }).join("");

    win.document.write(`<!DOCTYPE html><html><head><title>CaCart Demand Forecast Report</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1f2937;background:#fff;padding:36px 40px}
  .report{max-width:1180px;margin:0 auto}
  .report-header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:18px}
  .header-left .logo{height:46px;width:auto;display:block;margin-bottom:10px}
  .header-left .meta{font-size:12px;color:#475569;line-height:1.7}
  .header-right{text-align:right}
  .header-right .title{font-size:26px;font-weight:800;color:#0B1F3A;letter-spacing:.6px;font-family:Georgia,'Times New Roman',serif}
  .header-right .meta{font-size:12px;color:#475569;line-height:1.7;margin-top:4px}
  .filter-strip{background:#eff4fb;border-top:1px solid #d6e1ef;border-bottom:1px solid #d6e1ef;padding:10px 14px;font-size:12px;color:#0B1F3A;margin-bottom:18px}
  .filter-strip strong{font-weight:700;margin-right:6px}
  .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:18px}
  .sum-card{border:1px solid;padding:18px 20px;border-radius:4px;text-align:center}
  .sum-card .label{font-size:10.5px;font-weight:700;letter-spacing:1.2px;color:#64748b;margin-bottom:8px}
  .sum-card .value{font-size:22px;font-weight:800;font-family:Georgia,'Times New Roman',serif}
  .sum-card.products{background:#f3f6fb;border-color:#dde5f0}
  .sum-card.products .value{color:#0B1F3A}
  .sum-card.confirmed{background:#eef4fc;border-color:#cfdcef}
  .sum-card.confirmed .value{color:#1d4ed8}
  .sum-card.predicted{background:#eafaf0;border-color:#c5ecd2}
  .sum-card.predicted .value{color:#15803d}
  .sum-card.final{background:#fdf3e7;border-color:#f3d8b5}
  .sum-card.final .value{color:#b45309}
  table{width:100%;border-collapse:collapse;font-size:12.5px}
  thead th{background:#0B1F3A;color:#fff;font-size:10.5px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:12px 12px;text-align:left;border-bottom:1px solid #0B1F3A}
  thead th.right{text-align:right}
  tbody tr:nth-child(even) td{background:#f9fafb}
  tbody td{padding:14px 12px;border-bottom:1px solid #e5e7eb;vertical-align:middle}
  tbody td.num{color:#94a3b8;font-size:11px;width:36px}
  tbody td.right{text-align:right}
  tbody td.prod{font-weight:600;color:#1f2937}
  tbody td.amt{font-weight:700;color:#1f2937}
  tbody td.pred{font-weight:700;color:#7c3aed}
  tbody td.final{font-weight:700;color:#0B1F3A}
  .priority{display:inline-block;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700}
  .priority.high{color:#b91c1c}
  .priority.med{color:#b45309}
  .priority.low{color:#15803d}
  .trend{font-size:11.5px;font-weight:600}
  .trend.up{color:#15803d}
  .trend.down{color:#b91c1c}
  .trend.stable{color:#64748b}
  tfoot td{background:#0B1F3A;color:#fff;font-size:12.5px;font-weight:700;padding:14px 12px;letter-spacing:.4px}
  tfoot td.right{text-align:right}
  .divider{height:1px;background:#e5e7eb;margin:28px 0 14px 0}
  .footer{text-align:center;font-size:11px;color:#64748b;padding-top:6px}
  .footer .brand{color:#0B1F3A;font-weight:700}
  @media print{body{padding:18px}}
</style>
</head><body>
<div class="report">
  <div class="report-header">
    <div class="header-left">
      <img src="${logoUrl}" alt="CaCart" class="logo" />
      <div class="meta">
        CaCart — Smart Inventory & Demand Forecasting<br/>
        support@cacart.com<br/>
      </div>
    </div>
    <div class="header-right">
      <div class="title">DEMAND FORECAST REPORT</div>
      <div class="meta">
        Date: ${reportDate}<br/>
        Prepared By: ${preparedBy}
      </div>
    </div>
  </div>
  <div class="filter-strip"><strong>Filter:</strong>${filterLabel}</div>
  <div class="summary">
    <div class="sum-card products"><div class="label">TOTAL PRODUCTS</div><div class="value">${totalProducts}</div></div>
    <div class="sum-card confirmed"><div class="label">TOTAL CONFIRMED</div><div class="value">${fmt(totalConfirmed)}</div></div>
    <div class="sum-card predicted"><div class="label">TOTAL PREDICTED</div><div class="value">${fmt(totalPredicted)}</div></div>
    <div class="sum-card final"><div class="label">TOTAL FINAL</div><div class="value">${fmt(totalFinal)}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Product</th>
        <th>Unit</th>
        <th class="right">Confirmed</th>
        <th class="right">Predicted</th>
        <th class="right">Final</th>
        <th class="right">Orders</th>
        <th>Priority</th>
        <th>Trend</th>
      </tr>
    </thead>
    <tbody>${rowsHtml || `<tr><td colspan="9" style="text-align:center;padding:24px;color:#94a3b8">No forecast data</td></tr>`}</tbody>
    <tfoot>
      <tr>
        <td colspan="3">TOTALS &nbsp;|&nbsp; ${totalProducts} product${totalProducts === 1 ? "" : "s"}</td>
        <td class="right">${fmt(totalConfirmed)}</td>
        <td class="right">${fmt(totalPredicted)}</td>
        <td class="right">${fmt(totalFinal)}</td>
        <td colspan="3"></td>
      </tr>
    </tfoot>
  </table>
  <div class="divider"></div>
  <div class="footer">
    <div><span class="brand">CaCart</span> — Smart Inventory & Demand Forecasting</div>
    <div>Generated via CaCart System &nbsp;•&nbsp; © ${dayjs().format("YYYY")}</div>
  </div>
</div>
<script>window.onload=function(){window.print()}</script>
</body></html>`);
    win.document.close();
  };

  // ── RENDER ──
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh", bgcolor: "#f8f9fb" }}>
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>

        {/* ═══ HEADER ═══ */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3.5 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 3, background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(30,41,59,0.25)" }}>
            <AssessmentIcon sx={{ fontSize: 24, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography fontSize={26} fontWeight={800} color="#0f172a" letterSpacing="-0.03em" sx={{ lineHeight: 1.1 }}>Demand Forecast</Typography>
              {locked && <Chip icon={<LockIcon sx={{ fontSize: 12 }} />} label="Locked" size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#dcfce7", color: "#16a34a", "& .MuiChip-icon": { color: "#16a34a" } }} />}
            </Box>
            <Typography fontSize={13} color="#94a3b8" fontWeight={400} mt={0.25}>Aggregate confirmed orders with AI predictions for procurement planning</Typography>
          </Box>
          {generated && (
            <ToggleButtonGroup value={mode} exclusive onChange={(_, v) => v && setMode(v)} size="small"
              sx={{ bgcolor: "#f1f5f9", borderRadius: 3, p: 0.5, "& .MuiToggleButton-root": { border: "none", borderRadius: "10px !important", px: 2.5, py: 0.75, gap: 0.75 },
                "& .Mui-selected": { bgcolor: "#fff !important", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", color: "#0f172a !important" },
                "& .MuiToggleButton-root:not(.Mui-selected)": { color: "#94a3b8" } }}>
              <ToggleButton value="forecast" sx={{ textTransform: "none", fontWeight: 600, fontSize: 13 }}>
                <AssessmentIcon sx={{ fontSize: 16 }} /> Forecast
              </ToggleButton>
              <ToggleButton value="procurement" sx={{ textTransform: "none", fontWeight: 600, fontSize: 13 }}>
                <ShoppingCartIcon sx={{ fontSize: 16 }} /> Procurement
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* ═══ FILTERS ═══ */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 175 }}>
              <Typography fontSize={10} fontWeight={600} color="#94a3b8" mb={0.75} sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 10 }} /> Delivery Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={deliveryDate ? dayjs(deliveryDate) : null}
                  onChange={(d) => setDeliveryDate(d ? (d as any).format("YYYY-MM-DD") : "")}
                  format="MMM D, YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "10px",
                          bgcolor: "#fff",
                          fontSize: 13,
                          transition: "all 0.18s ease",
                          "& fieldset": { borderColor: "#e2e8f0", transition: "all 0.18s ease" },
                          "&:hover fieldset": { borderColor: "#cbd5e1" },
                          "&.Mui-focused fieldset": { borderColor: "#0B1F3A", borderWidth: "1.5px" },
                          "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(11,31,58,0.08)" },
                        },
                      },
                    },
                    desktopPaper: {
                      sx: {
                        mt: 1,
                        borderRadius: "14px",
                        p: 1,
                        border: "none",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                        transformOrigin: "top center",
                        animation: "fadeInScale 0.18s ease",
                        "@keyframes fadeInScale": {
                          from: { opacity: 0, transform: "scale(0.96)" },
                          to: { opacity: 1, transform: "scale(1)" },
                        },
                        // Header (month + nav)
                        "& .MuiPickersCalendarHeader-root": { px: 1, mt: 0.5, mb: 0.5 },
                        "& .MuiPickersCalendarHeader-label": {
                          fontSize: 15, fontWeight: 600, color: "#0B1F3A",
                        },
                        "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                          borderRadius: "8px",
                          color: "#0B1F3A",
                          "&:hover": { bgcolor: "#f1f5f9" },
                        },
                        // Week labels
                        "& .MuiDayCalendar-weekDayLabel": {
                          fontSize: 12, fontWeight: 500, color: "#94a3b8",
                          width: 36, height: 36,
                        },
                        // Day cells
                        "& .MuiPickersDay-root": {
                          width: 36, height: 36, fontSize: 13,
                          borderRadius: "8px",
                          color: "#334155",
                          margin: "2px",
                          transition: "all 0.15s ease",
                          "&:hover": { bgcolor: "#f1f5f9" },
                          "&.Mui-disabled": { color: "#cbd5f5" },
                        },
                        "& .MuiPickersDay-today:not(.Mui-selected)": {
                          border: "1px solid #0B1F3A",
                          bgcolor: "transparent",
                        },
                        "& .MuiPickersDay-root.Mui-selected": {
                          bgcolor: "#0B1F3A !important",
                          color: "#fff !important",
                          fontWeight: 600,
                          "&:hover": { bgcolor: "#0F2A4A !important" },
                        },
                        // Action bar (Today / Clear)
                        "& .MuiDialogActions-root": {
                          padding: "8px 12px",
                          borderTop: "1px solid #f1f5f9",
                          "& .MuiButton-root": {
                            color: "#0B1F3A",
                            fontWeight: 600,
                            textTransform: "none",
                            fontSize: 13,
                            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                          },
                        },
                      },
                    },
                    actionBar: { actions: ["today", "clear"] },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ minWidth: 165 }}>
              <Typography fontSize={10} fontWeight={600} color="#94a3b8" mb={0.75} sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 0.5 }}>
                <AltRouteIcon sx={{ fontSize: 10 }} /> Route
              </Typography>
              <Select fullWidth size="small" value={routeId} onChange={e => setRouteId(e.target.value as string)}
                displayEmpty sx={{ borderRadius: 2.5, bgcolor: "#fafbfc", "& fieldset": { borderColor: "rgba(0,0,0,0.08)" }, "&:hover": { bgcolor: "#f1f5f9" } }}>
                <MenuItem value="">All Routes</MenuItem>
                {routes.map(r => <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>)}
              </Select>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.75, borderRadius: 2.5, bgcolor: includeAi ? "#faf5ff" : "#fafbfc", border: `1px solid ${includeAi ? "#e9d5ff" : "rgba(0,0,0,0.06)"}`, transition: "all 0.2s" }}>
              <Switch checked={includeAi} onChange={(_, v) => setIncludeAi(v)} size="small"
                sx={{ mr: 0.5, "& .MuiSwitch-switchBase.Mui-checked": { color: "#8b5cf6" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#8b5cf6" } }} />
              <AutoAwesomeIcon sx={{ fontSize: 14, color: includeAi ? "#8b5cf6" : "#cbd5e1", mr: 0.5 }} />
              <Typography fontSize={12} fontWeight={600} color={includeAi ? "#7c3aed" : "#94a3b8"}>AI</Typography>
            </Box>
            {isProcure && (
              <Box sx={{ width: 105 }}>
                <Typography fontSize={10} fontWeight={600} color="#94a3b8" mb={0.75} sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>Buffer</Typography>
                <TextField size="small" type="number" value={bufferPct} onChange={e => setBufferPct(Math.max(0, Math.min(50, Number(e.target.value))))}
                  InputProps={{ endAdornment: <InputAdornment position="end"><Typography fontSize={11} color="#94a3b8" fontWeight={600}>%</Typography></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#fafbfc", "& fieldset": { borderColor: "rgba(0,0,0,0.08)" } } }} />
              </Box>
            )}
            <Box sx={{ ml: "auto" }}>
              <Button variant="contained" onClick={generate} disabled={loading || !deliveryDate} disableElevation
                startIcon={loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2.5, py: 1.2, px: 3.5, fontWeight: 700, fontSize: 13, bgcolor: "#0f172a",
                  "&:hover": { bgcolor: "#1e293b", boxShadow: "0 4px 14px rgba(15,23,42,0.3)" },
                  textTransform: "none", letterSpacing: "-0.01em", transition: "all 0.2s" }}>
                {loading ? "Generating..." : "Generate Forecast"}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* ═══ AI CONFIDENCE FILTER ═══ */}
        {generated && includeAi && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, px: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#faf5ff", px: 1.5, py: 0.5, borderRadius: 2 }}>
              <AutoAwesomeIcon sx={{ fontSize: 13, color: "#8b5cf6" }} />
              <Typography fontSize={11} fontWeight={600} color="#7c3aed">Min Confidence</Typography>
            </Box>
            <Slider value={minConfidence} onChange={(_, v) => setMinConfidence(v as number)} min={0} max={100} step={5}
              valueLabelDisplay="auto" valueLabelFormat={v => `${v}%`}
              sx={{ maxWidth: 200, "& .MuiSlider-thumb": { width: 14, height: 14, bgcolor: "#8b5cf6", boxShadow: "0 2px 6px rgba(139,92,246,0.4)" }, "& .MuiSlider-track": { bgcolor: "#8b5cf6", border: "none" }, "& .MuiSlider-rail": { bgcolor: "#e2e8f0" } }} />
            <Typography fontSize={11} color="#8b5cf6" fontWeight={700}>{minConfidence}%</Typography>
            {minConfidence > 0 && <Typography fontSize={11} color="#94a3b8">{filteredData.length}/{data.length}</Typography>}
          </Box>
        )}

        {/* ═══ RISK ALERTS ═══ */}
        {generated && riskAlerts.length > 0 && showAlerts && (
          <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: "1px solid #fde68a", bgcolor: "#fffdf7", boxShadow: "0 1px 3px rgba(251,191,36,0.1)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: 1.5, bgcolor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FlashOnIcon sx={{ fontSize: 14, color: "#d97706" }} />
              </Box>
              <Typography fontSize={12} fontWeight={700} color="#92400e">Insights</Typography>
              <Chip label={riskAlerts.length} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 800, bgcolor: "#fbbf24", color: "#78350f", ml: -0.5 }} />
              <Box sx={{ ml: "auto" }}><Button size="small" onClick={() => setShowAlerts(false)} sx={{ textTransform: "none", fontSize: 10, color: "#b45309", minWidth: "auto", fontWeight: 600 }}>Dismiss</Button></Box>
            </Box>
            <Grid container spacing={1.5}>
              {riskAlerts.map((al, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box sx={{ display: "flex", gap: 1.25, p: 1.5, borderRadius: 2.5, bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.04)" }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 2, bgcolor: al.type === "error" ? "#fef2f2" : al.type === "warning" ? "#fff7ed" : "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: al.type === "error" ? "#dc2626" : al.type === "warning" ? "#ea580c" : "#3b82f6" }}>
                      {al.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontSize={12} fontWeight={700} color="#1e293b" lineHeight={1.3}>{al.title}</Typography>
                      <Typography fontSize={11} color="#94a3b8" noWrap title={al.detail} lineHeight={1.4} mt={0.25}>{al.detail}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* ═══ ORDER COMPLETENESS ═══ */}
        {generated && summary && summary.total_restaurants_on_route > 0 && (
          <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${completeness >= 80 ? "#bbf7d0" : completeness >= 50 ? "#fed7aa" : "#fecaca"}`, bgcolor: completeness >= 80 ? "#f0fdf4" : completeness >= 50 ? "#fffbeb" : "#fef2f2", boxShadow: `0 1px 3px ${completeness >= 80 ? "rgba(22,163,74,0.06)" : completeness >= 50 ? "rgba(234,88,12,0.06)" : "rgba(220,38,38,0.06)"}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                {completeness >= 80 ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} /> : <WarningAmberIcon sx={{ fontSize: 18, color: compColor }} />}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">
                    {summary.ordered_restaurants} <Typography component="span" fontSize={12} color="#94a3b8" fontWeight={400}>of</Typography> {summary.total_restaurants_on_route} <Typography component="span" fontSize={12} color="#94a3b8" fontWeight={400}>restaurants ordered</Typography>
                  </Typography>
                  <Chip label={`${completeness}%`} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: compColor, color: "#fff", boxShadow: `0 2px 6px ${compColor}40` }} />
                </Box>
                <LinearProgress variant="determinate" value={completeness} sx={{ height: 6, borderRadius: 3, bgcolor: "rgba(0,0,0,0.06)", "& .MuiLinearProgress-bar": { bgcolor: compColor, borderRadius: 3 } }} />
              </Box>
              {summary.missing_restaurants.length > 0 && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setShowMissing(v => !v)}
                    sx={{ ...btnOutline, borderColor: compColor + "40", color: compColor, fontSize: 11 }}>
                    {showMissing ? "Hide" : `${summary.missing_restaurants.length} Missing`}
                  </Button>
                  <Tooltip title="Send reminder to missing restaurants"><IconButton size="small" sx={{ bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.06)", width: 32, height: 32, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}><NotificationsActiveIcon sx={{ fontSize: 15, color: "#ea580c" }} /></IconButton></Tooltip>
                </Box>
              )}
            </Box>
            <Collapse in={showMissing}><Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.5 }}>{summary.missing_restaurants.map(m => (<Chip key={m.id} label={m.name} size="small" variant="outlined" sx={{ height: 26, fontSize: 11, fontWeight: 600, borderColor: compColor + "60", color: compColor, bgcolor: "#fff" }} />))}</Box></Collapse>
          </Paper>
        )}

        {/* ═══ SUMMARY CARDS ═══ */}
        {summary && generated && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: "Products", value: filteredData.length, icon: <InventoryIcon />, gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)", bg: "#eff6ff" },
              { label: "Restaurants", value: `${summary.ordered_restaurants}/${summary.total_restaurants_on_route || summary.unique_restaurants}`, icon: <StorefrontIcon />, gradient: "linear-gradient(135deg, #16a34a, #15803d)", bg: "#f0fdf4" },
              { label: "Confirmed", value: totals.confirmed, icon: <ReceiptLongIcon />, gradient: "linear-gradient(135deg, #ea580c, #c2410c)", bg: "#fff7ed" },
              ...(includeAi ? [{ label: "AI Predicted", value: `+${totals.predicted}`, icon: <AutoAwesomeIcon />, gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)", bg: "#faf5ff" }] : []),
              ...(isProcure ? [{ label: `Buffer (${bufferPct}%)`, value: `+${procSummary?.totalBuffer || 0}`, icon: <ShoppingCartIcon />, gradient: "linear-gradient(135deg, #0891b2, #0e7490)", bg: "#ecfeff" }] : []),
              { label: isProcure ? "Purchase Total" : "Final Total", value: totals.final, icon: <AssessmentIcon />, gradient: "linear-gradient(135deg, #0f172a, #334155)", bg: "#f1f5f9" },
            ].map((c, i) => (
              <Grid item xs={6} sm={2} key={i}>
                <Paper elevation={0} sx={{ ...cardSx, p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: c.gradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 4px 12px ${c.gradient.includes("#3b82f6") ? "rgba(59,130,246,0.3)" : c.gradient.includes("#16a34a") ? "rgba(22,163,74,0.3)" : c.gradient.includes("#ea580c") ? "rgba(234,88,12,0.3)" : c.gradient.includes("#8b5cf6") ? "rgba(139,92,246,0.3)" : c.gradient.includes("#0891b2") ? "rgba(8,145,178,0.3)" : "rgba(15,23,42,0.2)"}` }}>
                    {React.cloneElement(c.icon as React.ReactElement, { sx: { fontSize: 20, color: "#fff" } })}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontSize={10} color="#94a3b8" fontWeight={600} noWrap sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>{c.label}</Typography>
                    <Typography fontSize={22} fontWeight={800} color="#0f172a" letterSpacing="-0.03em" lineHeight={1.1}>{c.value}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ═══ SMART ACTIONS ═══ */}
        {generated && filteredData.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <Typography fontSize={15} fontWeight={700} color="#0f172a" letterSpacing="-0.01em">{dayLabel}</Typography>
            <Chip label={`${filteredData.length} items`} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#f1f5f9", color: "#64748b" }} />
            {isProcure && <Chip label={`+${bufferPct}% buffer`} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#ecfeff", color: "#0891b2" }} />}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Button size="small" variant={locked ? "contained" : "outlined"} disableElevation
              startIcon={locked ? <LockIcon sx={{ fontSize: 13 }} /> : <LockOpenIcon sx={{ fontSize: 13 }} />} onClick={() => setLocked(v => !v)}
              sx={{ ...btnOutline, ...(locked ? { bgcolor: "#16a34a", color: "#fff", borderColor: "#16a34a", "&:hover": { bgcolor: "#15803d" } } : {}) }}>
              {locked ? "Locked" : "Lock"}
            </Button>
            <Button size="small" variant="outlined" startIcon={<LocalShippingIcon sx={{ fontSize: 13 }} />} onClick={exportCSV} sx={btnOutline}>Supplier Export</Button>
            {summary && summary.missing_restaurants.length > 0 && (
              <Button size="small" variant="outlined" startIcon={<NotificationsActiveIcon sx={{ fontSize: 13 }} />}
                sx={{ ...btnOutline, borderColor: "#fed7aa", color: "#ea580c", bgcolor: "#fffbeb", "&:hover": { bgcolor: "#fff7ed" } }}>
                Notify {summary.missing_restaurants.length}
              </Button>
            )}
            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 13 }} />} onClick={printForecast} sx={btnOutline}>Print</Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />} onClick={exportCSV} sx={btnOutline}>CSV</Button>
            </Box>
          </Box>
        )}

        {/* ═══ DATA TABLE ═══ */}
        {generated && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            {filteredData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 12 }}>
                <Box sx={{ width: 72, height: 72, borderRadius: 3, bgcolor: "#f1f5f9", display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 2.5 }}>
                  <InventoryIcon sx={{ fontSize: 30, color: "#cbd5e1" }} />
                </Box>
                <Typography fontSize={17} fontWeight={700} color="#475569" mb={0.5}>No forecast data</Typography>
                <Typography fontSize={13} color="#94a3b8">{minConfidence > 0 ? "Try lowering the confidence filter, or " : ""}try a different date or route</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thSx, width: 36 }} />
                      <TableCell sx={thSx}>Product</TableCell>
                      <TableCell sx={thSx} align="center">Unit</TableCell>
                      <TableCell sx={thSx} align="right">Confirmed</TableCell>
                      {includeAi && <TableCell sx={thSx} align="right">Predicted</TableCell>}
                      {isProcure && <TableCell sx={{ ...thSx, color: "#0891b2" }} align="right">Buffer</TableCell>}
                      <TableCell sx={{ ...thSx, bgcolor: isProcure ? "#f0fdf410" : "#fafbfc" }} align="right">{isProcure ? "Purchase" : "Final Qty"}</TableCell>
                      <TableCell sx={thSx} align="center">Orders</TableCell>
                      <TableCell sx={thSx} align="center">Priority</TableCell>
                      {includeAi && <TableCell sx={thSx}>Trend</TableCell>}
                      {includeAi && <TableCell sx={thSx}>Insight</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((item, idx) => {
                      const isExp = expanded.has(item.product_id);
                      const hasBd = item.breakdown.length > 0 || item.ai_predictions.length > 0;
                      const pq = getPurchaseQty(item);
                      const hasPred = item.predicted_adj > 0 && includeAi;
                      const TrIcon = item.ai_trend === "increasing" ? TrendingUpIcon : item.ai_trend === "decreasing" ? TrendingDownIcon : TrendingFlatIcon;
                      const trColor = item.ai_trend === "increasing" ? "#16a34a" : item.ai_trend === "decreasing" ? "#ef4444" : "#94a3b8";
                      const trBg = item.ai_trend === "increasing" ? "#f0fdf4" : item.ai_trend === "decreasing" ? "#fef2f2" : "#f8fafc";
                      const pr = getPriority(item, filteredData);
                      const shopCount = new Set(item.breakdown.map(b => b.shop_id)).size;

                      return (
                        <React.Fragment key={item.product_id}>
                          <TableRow sx={{ "&:hover": { bgcolor: "#fafbfe" }, cursor: hasBd ? "pointer" : "default", borderBottom: idx === filteredData.length - 1 ? "none" : undefined }} onClick={() => hasBd && toggleExpand(item.product_id)}>
                            <TableCell sx={{ py: 1.25, px: 0.5 }}>
                              {hasBd && <IconButton size="small" sx={{ width: 24, height: 24, transition: "transform 0.2s", transform: isExp ? "rotate(180deg)" : "rotate(0)" }}><ExpandMoreIcon sx={{ fontSize: 15, color: "#94a3b8" }} /></IconButton>}
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Typography fontSize={13} fontWeight={600} color="#1e293b" sx={{ textTransform: "capitalize" }}>{item.name}</Typography>
                                {hasPred && <Tooltip title="Includes AI prediction"><AutoAwesomeIcon sx={{ fontSize: 11, color: "#a78bfa" }} /></Tooltip>}
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.25 }}>
                              <Typography fontSize={11} color="#64748b" fontWeight={600} sx={{ bgcolor: "#f1f5f9", display: "inline-block", px: 1, py: 0.25, borderRadius: 1 }}>{item.unit}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.25 }}>
                              {item.confirmed_qty > 0 ? (
                                <Typography fontSize={14} fontWeight={700} color="#1e293b" sx={{ fontVariantNumeric: "tabular-nums" }}>{item.confirmed_qty}</Typography>
                              ) : (
                                <Tooltip title="No confirmed orders — AI-predicted"><Typography fontSize={11} color="#cbd5e1" fontWeight={500} fontStyle="italic">none</Typography></Tooltip>
                              )}
                            </TableCell>
                            {includeAi && (
                              <TableCell align="right" sx={{ py: 1.25 }}>
                                {hasPred ? (
                                  <Typography fontSize={13} fontWeight={700} color="#8b5cf6" sx={{ fontVariantNumeric: "tabular-nums", bgcolor: "#faf5ff", display: "inline-block", px: 0.75, py: 0.15, borderRadius: 1 }}>+{item.predicted_adj}</Typography>
                                ) : <Typography fontSize={12} color="#e2e8f0">—</Typography>}
                              </TableCell>
                            )}
                            {isProcure && <TableCell align="right" sx={{ py: 1.25 }}><Typography fontSize={12} color="#0891b2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>+{pq.buffer}</Typography></TableCell>}
                            <TableCell align="right" sx={{ py: 1.25 }}>
                              <Typography fontSize={18} fontWeight={800} color={isProcure ? "#16a34a" : "#0f172a"} sx={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>{isProcure ? pq.total : pq.base}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.25 }}>
                              {totalRest > 0 ? (
                                <Tooltip title={`${shopCount} of ${totalRest} restaurants ordered this`}>
                                  <Typography fontSize={12} fontWeight={600} color={shopCount > 0 ? "#3b82f6" : "#cbd5e1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                                    {shopCount}<Typography component="span" fontSize={10} color="#cbd5e1" fontWeight={400}>/{totalRest}</Typography>
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography fontSize={12} fontWeight={600} color={item.orders_count > 0 ? "#3b82f6" : "#cbd5e1"}>{item.orders_count}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.25 }}>
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, borderRadius: 1.5, bgcolor: pr.bg }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: pr.dot }} />
                                <Typography fontSize={10} fontWeight={700} color={pr.color}>{pr.label}</Typography>
                              </Box>
                            </TableCell>
                            {includeAi && (
                              <TableCell sx={{ py: 1.25 }}>
                                {item.ai_confidence > 0 && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box sx={{ width: 26, height: 26, borderRadius: 2, bgcolor: trBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                      <TrIcon sx={{ fontSize: 14, color: trColor }} />
                                    </Box>
                                    <Box>
                                      <Typography fontSize={10} fontWeight={600} color={trColor} sx={{ textTransform: "capitalize", lineHeight: 1.1 }}>{item.ai_trend}</Typography>
                                      <Box sx={{ width: 32, height: 3, bgcolor: "#f1f5f9", borderRadius: 2, mt: 0.3, overflow: "hidden" }}>
                                        <Box sx={{ height: "100%", width: `${item.ai_confidence}%`, bgcolor: item.ai_confidence >= 70 ? "#16a34a" : item.ai_confidence >= 40 ? "#f59e0b" : "#94a3b8", borderRadius: 2 }} />
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                              </TableCell>
                            )}
                            {includeAi && <TableCell sx={{ py: 1.25 }}><Typography fontSize={11} color="#94a3b8" noWrap sx={{ maxWidth: 170 }} title={item.ai_reason}>{item.ai_reason}</Typography></TableCell>}
                          </TableRow>

                          {/* Expanded */}
                          {hasBd && (
                            <TableRow><TableCell colSpan={colCount} sx={{ p: 0, border: 0 }}>
                              <Collapse in={isExp} timeout={200}>
                                <Box sx={{ px: 5, py: 2, bgcolor: "#fafbfc", borderTop: "1px solid #f1f5f9" }}>
                                  {item.breakdown.length > 0 && (
                                    <Box sx={{ mb: item.ai_predictions.length > 0 ? 2 : 0 }}>
                                      <Typography fontSize={10} fontWeight={700} color="#64748b" mb={1} sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>Confirmed Orders</Typography>
                                      <Table size="small" sx={{ "& td, & th": { border: 0 } }}>
                                        <TableBody>
                                          {item.breakdown.map((b, i) => (
                                            <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f1f5f9" } }}>
                                              <TableCell sx={{ fontSize: 12, py: 0.6, fontWeight: 500, color: "#1e293b" }}>{b.shop_name}</TableCell>
                                              <TableCell sx={{ fontSize: 11, py: 0.6, color: "#94a3b8" }}>{b.route_name}</TableCell>
                                              <TableCell sx={{ fontSize: 13, fontWeight: 700, py: 0.6, color: "#0f172a" }} align="right">{b.qty}</TableCell>
                                              <TableCell sx={{ fontSize: 11, py: 0.6, color: "#94a3b8" }}>{b.unit}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}
                                  {includeAi && item.ai_predictions.length > 0 && (
                                    <Box>
                                      <Typography fontSize={10} fontWeight={700} color="#8b5cf6" mb={1} sx={{ display: "flex", alignItems: "center", gap: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                        <AutoAwesomeIcon sx={{ fontSize: 11 }} /> Predicted — Not Yet Ordered
                                      </Typography>
                                      <Table size="small" sx={{ "& td, & th": { border: 0 } }}>
                                        <TableBody>
                                          {item.ai_predictions.map((ap, i) => (
                                            <TableRow key={i} sx={{ "&:hover": { bgcolor: "#faf5ff" } }}>
                                              <TableCell sx={{ fontSize: 12, py: 0.6, fontWeight: 500 }}>{ap.shop_name}</TableCell>
                                              <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#8b5cf6", py: 0.6 }} align="right">+{ap.predicted_qty}</TableCell>
                                              <TableCell sx={{ py: 0.6 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                  <Box sx={{ width: 40, height: 3, bgcolor: "#f1f5f9", borderRadius: 2, overflow: "hidden" }}>
                                                    <Box sx={{ height: "100%", width: `${ap.confidence}%`, bgcolor: ap.confidence >= 70 ? "#16a34a" : ap.confidence >= 40 ? "#f59e0b" : "#94a3b8", borderRadius: 2 }} />
                                                  </Box>
                                                  <Typography fontSize={9} color="#94a3b8" fontWeight={600}>{ap.confidence}%</Typography>
                                                </Box>
                                              </TableCell>
                                              <TableCell sx={{ fontSize: 11, color: "#94a3b8", py: 0.6 }}>{ap.reason}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell></TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* ── Totals Row ── */}
                    <TableRow sx={{ "& td": { borderTop: "2px solid #e2e8f0", borderBottom: "none", py: 1.75, bgcolor: "#fafbfc" } }}>
                      <TableCell />
                      <TableCell><Typography fontSize={12} fontWeight={800} color="#0f172a" letterSpacing="0.02em">TOTAL</Typography></TableCell>
                      <TableCell />
                      <TableCell align="right"><Typography fontSize={15} fontWeight={800} color="#0f172a" sx={{ fontVariantNumeric: "tabular-nums" }}>{totals.confirmed}</Typography></TableCell>
                      {includeAi && <TableCell align="right"><Typography fontSize={14} fontWeight={700} color="#8b5cf6" sx={{ fontVariantNumeric: "tabular-nums" }}>+{totals.predicted}</Typography></TableCell>}
                      {isProcure && <TableCell align="right"><Typography fontSize={12} fontWeight={700} color="#0891b2">+{totals.buffer}</Typography></TableCell>}
                      <TableCell align="right"><Typography fontSize={22} fontWeight={800} color={isProcure ? "#16a34a" : "#0f172a"} sx={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.03em" }}>{totals.final}</Typography></TableCell>
                      <TableCell align="center"><Typography fontSize={12} fontWeight={700} color="#64748b">{totals.orders}</Typography></TableCell>
                      <TableCell />
                      {includeAi && <><TableCell /><TableCell /></>}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* ═══ INITIAL STATE ═══ */}
        {!generated && !loading && (
          <Paper elevation={0} sx={{ textAlign: "center", py: 14, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <Box sx={{ width: 80, height: 80, borderRadius: 4, background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
              <AssessmentIcon sx={{ fontSize: 36, color: "#94a3b8" }} />
            </Box>
            <Typography fontSize={22} fontWeight={800} color="#0f172a" mb={0.75} letterSpacing="-0.03em">Generate Your Forecast</Typography>
            <Typography fontSize={14} color="#94a3b8" mb={4} maxWidth={420} mx="auto" lineHeight={1.6}>
              Select a delivery date and route, then aggregate confirmed orders with AI-predicted demand into a procurement plan
            </Typography>
            <Button variant="contained" onClick={generate} disabled={!deliveryDate} disableElevation startIcon={<AssessmentIcon sx={{ fontSize: 20 }} />}
              sx={{ borderRadius: 3, py: 1.5, px: 5, fontWeight: 700, fontSize: 15, bgcolor: "#0f172a",
                "&:hover": { bgcolor: "#1e293b", boxShadow: "0 8px 24px rgba(15,23,42,0.3)", transform: "translateY(-1px)" },
                textTransform: "none", letterSpacing: "-0.01em", transition: "all 0.25s ease" }}>
              Generate Forecast
            </Button>
          </Paper>
        )}

      </Box>
    </Box>
  );
};

export default DemandForecast;
